<script setup lang="ts">
import {computed} from "vue";
import {ConversationMessage, ConversationMessageInteraction, MessageStatus} from "@/conversation/interfaces";
import {isRCSOutbound, isSMSOutbound} from "@/conversation/interfaces/guard";
import type {RcsPayload} from "@/notification-template/interfaces";
import ConversationMessagePreview from "@/conversation/components/ConversationMessagePreview.vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {ArrowRight, MousePointerClick, Reply} from "@lucide/vue";

const props = defineProps<{
    messages: ConversationMessage[];
}>();

type FlowStep =
    | {type: 'message'; message: ConversationMessage; date: string}
    | {type: 'interaction'; interaction: ConversationMessageInteraction; date: string};

/**
 * Interleave the outbound flow messages with the patient reactions they received
 * (replies and button interactions), in chronological order, to render the whole flow path.
 */
const thread = computed<FlowStep[]>(() => {
    return props.messages.flatMap((message) => {
        const reactions: FlowStep[] = [
            ...(message.replies ?? []).map((reply): FlowStep => ({
                type: 'message',
                message: reply,
                date: reply.created_at
            })),
            ...(isRCSOutbound(message) ? (message.interactions ?? []) : []).map((interaction): FlowStep => ({
                type: 'interaction',
                interaction,
                date: interaction.created_at
            }))
        ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        return [
            {type: 'message', message, date: message.created_at} satisfies FlowStep,
            ...reactions
        ];
    });
});

/**
 * Extract a short label for a message step of the flow.
 */
const label = (message: ConversationMessage): string => {
    if (message.direction === 'inbound') {
        return (message.payload as {title?: string})?.title ?? 'Réponse';
    }

    if (isSMSOutbound(message)) {
        return message.payload.text;
    }

    const payload = message.payload as RcsPayload;

    switch (payload.type) {
        case 'text':
            return payload.content;
        case 'card':
            return payload.title;
        case 'carousel':
            return payload.cards[0]?.title ?? 'Carrousel';
        default:
            return 'Message';
    }
};

/**
 * Status dot color of an outbound step, aligned with the status badge grouping.
 */
const dotClass = (status: MessageStatus): string => {
    if (status === 'read') {
        return 'bg-green-500';
    }

    if (['delivered', 'submitted'].includes(status)) {
        return 'bg-blue-500';
    }

    if (['failed', 'rejected', 'undeliverable'].includes(status)) {
        return 'bg-red-500';
    }

    return 'bg-slate-400';
};

const formatTime = (date: string): string => {
    return new Date(date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};
</script>

<template>
  <div class="flex items-start gap-1.5 flex-wrap py-4">
    <template
      v-for="(step, index) in thread"
      :key="step.type === 'message' ? `m-${step.message.id}` : `i-${step.interaction.id}`"
    >
      <ArrowRight v-if="index > 0" class="w-4 h-4 mt-1.5 shrink-0 text-gray-300" />
      <TooltipProvider v-if="step.type === 'message'">
        <Tooltip>
          <TooltipTrigger as-child>
            <div class="flex flex-col items-center gap-0.5 cursor-default">
              <div
                class="flex items-center gap-1.5 rounded-full border px-3 py-1"
                :class="step.message.direction === 'outbound'
                  ? 'border-primary/30 bg-primary/5 text-primary'
                  : 'border-gray-200 bg-gray-100 text-gray-700'"
              >
                <span
                  v-if="step.message.direction === 'outbound'"
                  class="w-2 h-2 rounded-full shrink-0"
                  :class="dotClass(step.message.status)"
                />
                <Reply v-else class="w-3.5 h-3.5 shrink-0 text-gray-500" />
                <span class="truncate max-w-44 text-xs font-medium">
                  <template v-if="step.message.direction === 'inbound'">« {{ label(step.message) }} »</template>
                  <template v-else>{{ label(step.message) }}</template>
                </span>
              </div>
              <time class="text-[10px] text-gray-400">{{ formatTime(step.date) }}</time>
            </div>
          </TooltipTrigger>
          <TooltipContent class="bg-white max-w-sm shadow-2xl" avoid-collisions>
            <ConversationMessagePreview :message="step.message" />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <div v-else class="flex flex-col items-center gap-0.5 cursor-default" title="A cliqué sur ce bouton">
        <div class="flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-indigo-700">
          <MousePointerClick class="w-3.5 h-3.5 shrink-0" />
          <span class="truncate max-w-44 text-xs font-medium">{{ step.interaction.text ?? 'Clic' }}</span>
        </div>
        <time class="text-[10px] text-gray-400">{{ formatTime(step.date) }}</time>
      </div>
    </template>
  </div>
</template>

<style scoped>

</style>
